'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["isOver"];
import PropTypes from 'prop-types';
import { Component } from 'react';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import styled from 'styled-components';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import classnames from 'classnames';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIMedia from 'UIComponents/layout/UIMedia';
import UIMediaLeft from 'UIComponents/layout/UIMediaLeft';
import UIMediaBody from 'UIComponents/layout/UIMediaBody';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UIButton from 'UIComponents/button/UIButton';
import UIProgress from 'UIComponents/progress/UIProgress';
import { fileSize } from 'FileManagerCore/utils/fileSize';
import { isFile } from 'FileManagerCore/utils/FoldersAndFiles';
import { getFileAccess } from 'FileManagerCore/utils/fileAccessibility';
import FileDragAndDrop from 'FileManagerCore/components/FileDragAndDrop';
import TableActionButtons, { TABLE_ACTIONS_CLASSNAME } from './TableActionButtons';
import { navigateToFolder } from '../actions/Folders';
import { hasParentWithClass } from '../utils/dom';
import FileFolderRowPath from './FileFolderRowPath';
import UsageTracker from '../utils/usageTracker';
import { getIsSearchingOrFiltering } from '../selectors/FoldersAndFiles';
import { getSearchExtension, getSearchQuery } from 'FileManagerCore/selectors/Files';
import HighlightedName from './HighlightedName';
import { getType, getIsTemporaryUploadingFile } from 'FileManagerCore/utils/file';
import { FILE_MANAGER_DASHBOARD_DEFAULT_ACCESS } from '../Constants';
import RestrictedAccessIcon from 'FileManagerCore/components/RestrictedAccessIcon';
import { getIsTeamsRequestSucceeded, getPortalTeams } from 'FileManagerCore/selectors/Teams';
import { fetchTeams } from 'FileManagerCore/actions/Teams';
import ThumbnailPreviewOrIcon from 'file-manager-components/thumbnail/ThumbnailPreviewOrIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ROW_CLASSNAME = 'file-folder-table-row';
export const FILE_ROW_CLASSNAME = 'is-file';
export const FOLDER_ROW_CLASSNAME = 'is-folder';
const ThumbnailWrapper = styled.div.withConfig({
  displayName: "FileFolderTableRow__ThumbnailWrapper",
  componentId: "sc-1g6xyk0-0"
})(["width:34px;height:34px;"]);
class FileFolderTableRow extends Component {
  constructor(...args) {
    super(...args);
    this.handleSelectFile = event => {
      if (hasParentWithClass(event.target, TABLE_ACTIONS_CLASSNAME) || !hasParentWithClass(event.target, ROW_CLASSNAME)) {
        return;
      }
      this.handleClickFileDetails();
    };
    this.handleClickFileDetails = () => {
      const {
        object,
        onSelectFile
      } = this.props;
      UsageTracker.track('fileManagerExploreFiles', {
        action: 'click-file-details-button',
        fileType: getType(object.get('extension')),
        fileExtension: object.get('extension')
      });
      onSelectFile(object);
    };
    this.handleBulkSelectFile = event => {
      const {
        object,
        onBulkSelectFile
      } = this.props;
      onBulkSelectFile(object, event.target.checked ? 'add' : 'remove');
    };
    this.handleCheckboxClick = event => {
      event.stopPropagation();
    };
    this.handleFolderClick = () => {
      const {
        object,
        dispatchNavigateToFolder
      } = this.props;
      UsageTracker.track('fileManagerExploreFiles', {
        action: 'click-folder-row'
      });
      dispatchNavigateToFolder(object.get('id'));
    };
  }
  getI18nKey(suffix) {
    return `FileManagerDashboard.fileVisibility.${suffix}`;
  }
  isBulkSelectionDisabled() {
    const {
      bulkSelected,
      object,
      reachedSelectionLimit
    } = this.props;
    return object.has('progress') || reachedSelectionLimit && !bulkSelected;
  }
  shouldShowFolderPaths() {
    const {
      isSearching,
      object
    } = this.props;
    return isSearching && Boolean(object.get('folder_id') || object.get('parent_folder_id'));
  }
  renderFileExtension(object) {
    const extension = object.get('extension');
    const fileSearchExtension = this.props.searchExtension;
    if (fileSearchExtension) {
      return /*#__PURE__*/_jsx(HighlightedName, {
        query: extension,
        name: extension
      });
    }
    return extension;
  }
  renderFolderBreadcrumbs() {
    if (!this.shouldShowFolderPaths()) {
      return null;
    }
    return /*#__PURE__*/_jsx("div", {
      className: "search-folder-breadcrumbs",
      children: /*#__PURE__*/_jsx(FileFolderRowPath, {
        object: this.props.object,
        navigateToFolder: this.props.dispatchNavigateToFolder
      })
    });
  }
  renderName(object) {
    const name = object.get('name');
    if (isFile(object)) {
      return /*#__PURE__*/_jsxs("div", {
        children: [this.renderFolderBreadcrumbs(), /*#__PURE__*/_jsx(UIButton, {
          "data-test-id": "file-row",
          use: "unstyled",
          children: /*#__PURE__*/_jsx(HighlightedName, {
            name: name,
            query: this.props.searchQuery
          })
        })]
      });
    }
    return /*#__PURE__*/_jsxs("div", {
      children: [this.renderFolderBreadcrumbs(), /*#__PURE__*/_jsx(UIButton, {
        "data-test-id": "folder-row",
        onClick: this.handleFolderClick,
        use: "link",
        children: /*#__PURE__*/_jsx(HighlightedName, {
          name: name,
          query: this.props.searchQuery || this.props.searchExtension
        })
      })]
    });
  }
  renderUpdatedAt(object) {
    return I18n.moment(object.get('updated')).format('MMMM D, YYYY');
  }
  renderSize(object) {
    if (isFile(object) && object.get('size')) {
      return fileSize(object.get('size'));
    }
    return '';
  }
  renderFilePreview(file) {
    return /*#__PURE__*/_jsx(ThumbnailWrapper, {
      children: /*#__PURE__*/_jsx(ThumbnailPreviewOrIcon, {
        file: file.toJS()
      })
    });
  }
  renderFolderIcon() {
    return /*#__PURE__*/_jsx("a", {
      onClick: this.handleFolderClick,
      children: /*#__PURE__*/_jsx(UIIcon, {
        name: "folder",
        size: "xs"
      })
    });
  }
  renderFileVisibility(file) {
    if (isFile(file)) {
      const fileAccess = getIsTemporaryUploadingFile(file) ? FILE_MANAGER_DASHBOARD_DEFAULT_ACCESS : getFileAccess(file);
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: this.getI18nKey(fileAccess)
      });
    }
    return '';
  }
  renderRow(isOver = false, props) {
    const {
      object,
      selected,
      bulkSelected,
      teams,
      isTeamsRequestSucceeded,
      handleFetchTeams
    } = this.props;
    return /*#__PURE__*/_jsxs("tr", Object.assign({}, props, {
      className: classnames(ROW_CLASSNAME, isFile(object) ? FILE_ROW_CLASSNAME : FOLDER_ROW_CLASSNAME, isOver && 'is-over', selected && 'is-selected'),
      "data-test-id": "file-folder-table-row",
      "data-test-object-id": object.get('id'),
      onClick: isFile(object) && !object.has('progress') ? this.handleSelectFile : null,
      children: [/*#__PURE__*/_jsx("td", {
        className: "p-all-0 text-center",
        children: /*#__PURE__*/_jsx(UICheckbox, {
          className: "m-x-auto",
          disabled: this.isBulkSelectionDisabled(),
          onClick: this.handleCheckboxClick,
          onChange: this.handleBulkSelectFile,
          checked: bulkSelected,
          "aria-label": I18n.text('FileManagerDashboard.actions.select'),
          "data-test-id": "file-folder-checkbox"
        })
      }), /*#__PURE__*/_jsx("td", {
        className: "p-y-0 p-right-0",
        children: /*#__PURE__*/_jsxs(UIFlex, {
          align: "center",
          justify: "between",
          children: [/*#__PURE__*/_jsxs(UIMedia, {
            className: "flex-grow-1",
            align: "center",
            children: [/*#__PURE__*/_jsx(UIMediaLeft, {
              className: "file-folder-table-row--media-left align-center justify-center",
              children: isFile(object) ? this.renderFilePreview(object) : this.renderFolderIcon()
            }), /*#__PURE__*/_jsx(UIMediaBody, {
              className: "align-center justify-center p-y-4",
              "data-test-id": "file-folder-name",
              children: this.renderName(object)
            })]
          }), object.has('progress') ? /*#__PURE__*/_jsx(UIProgress, {
            "aria-label": I18n.text('FileManagerDashboard.progress'),
            "data-test-id": "file-row-progress",
            wrapperClassName: "file-folder-table-row--upload-progress",
            value: object.get('progress')
          }) : /*#__PURE__*/_jsx(TableActionButtons, {
            object: object,
            onClickFileDetails: this.handleClickFileDetails,
            shrinkOnHide: true
          }), !isFile(object) && object.get('teams').size > 0 && /*#__PURE__*/_jsx("div", {
            className: "p-left-4",
            children: /*#__PURE__*/_jsx(RestrictedAccessIcon, {
              folder: object,
              teams: teams,
              isTeamsRequestSucceeded: isTeamsRequestSucceeded,
              fetchTeams: handleFetchTeams
            })
          })]
        })
      }), /*#__PURE__*/_jsx("td", {
        children: this.renderFileVisibility(object)
      }), /*#__PURE__*/_jsx("td", {
        children: this.renderSize(object)
      }), /*#__PURE__*/_jsx("td", {
        children: this.renderFileExtension(object)
      }), /*#__PURE__*/_jsx("td", {
        children: this.renderUpdatedAt(object)
      })]
    }));
  }
  render() {
    const {
      onDropFiles,
      object
    } = this.props;
    if (isFile(object)) {
      return this.renderRow();
    }
    return /*#__PURE__*/_jsx(FileDragAndDrop, {
      onDropFiles: files => onDropFiles(files, object),
      children: _ref => {
        let {
            isOver
          } = _ref,
          props = _objectWithoutPropertiesLoose(_ref, _excluded);
        return this.renderRow(isOver, props);
      }
    });
  }
}
FileFolderTableRow.propTypes = {
  object: PropTypes.instanceOf(Immutable.Map).isRequired,
  selected: PropTypes.bool.isRequired,
  reachedSelectionLimit: PropTypes.bool.isRequired,
  bulkSelected: PropTypes.bool.isRequired,
  isSearching: PropTypes.bool.isRequired,
  onDropFiles: PropTypes.func.isRequired,
  onSelectFile: PropTypes.func.isRequired,
  onBulkSelectFile: PropTypes.func.isRequired,
  dispatchNavigateToFolder: PropTypes.func.isRequired,
  searchQuery: PropTypes.string,
  searchExtension: PropTypes.string,
  handleFetchTeams: PropTypes.func.isRequired,
  isTeamsRequestSucceeded: PropTypes.bool.isRequired,
  teams: PropTypes.object.isRequired
};
const mapStateToProps = state => ({
  isSearching: getIsSearchingOrFiltering(state),
  searchQuery: getSearchQuery(state),
  searchExtension: getSearchExtension(state),
  teams: getPortalTeams(state),
  isTeamsRequestSucceeded: getIsTeamsRequestSucceeded(state)
});
const mapDispatchToProps = {
  dispatchNavigateToFolder: navigateToFolder,
  handleFetchTeams: fetchTeams
};
export default connect(mapStateToProps, mapDispatchToProps)(FileFolderTableRow);